import { Alert, Form, Input, Modal } from 'antd';
import { useEffect } from 'react';
import { DriverSearchSelect } from '../../drivers/public/DriverSearchSelect';

interface FormValues {
  newDriverId: string;
  reason: string;
}

interface HandoverModalProps {
  open: boolean;
  currentDriverName: string | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: FormValues) => Promise<boolean>;
  onCancel: () => void;
}

/** The new driver is judged by the same eligibility engine as at dispatch; the truck and trailer stay with the trip. */
export function HandoverModal({ open, currentDriverName, submitting, error, onSubmit, onCancel }: HandoverModalProps) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  async function submit() {
    const values = await form.validateFields();
    if (await onSubmit(values)) form.resetFields();
  }

  return (
    <Modal open={open} title="Hand over this trip" onOk={submit} onCancel={onCancel} confirmLoading={submitting} destroyOnClose okText="Hand over">
      <p className="mb-4 text-sm text-muted">
        {currentDriverName ?? 'The current driver'} stops being able to record on this trip from now. The truck and trailer stay with it, and the new driver
        picks up from where it is.
      </p>
      {error && <Alert type="error" showIcon message={error} className="mb-4" />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="newDriverId" label="New driver" rules={[{ required: true, message: 'Choose the driver taking over' }]}>
          <DriverSearchSelect />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, whitespace: true, message: 'Say why the trip is changing driver' },
            { max: 500, message: 'At most 500 characters' },
          ]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
